import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/api/ApiClient';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import ActionIcon from '@/components/ui/ActionIcon/ActionIcon';
import CustomDialog from '@/components/ui/Dialog/Dialog';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import eye from '@/assets/icons/eye.svg';
import download from '@/assets/icons/download.svg';

interface DonorDonationsReportControlProps {
  /** The API path of the years the donor gave in, newest first - the only years the picker offers. */
  yearsUrl: string;
  /** The API path of the report PDF for a year (HAT-1462) - fund-scoped for a coordinator, across funds in management. */
  reportUrlOf: (year: number) => string;
  /** The API path of the same report as page images, which is what the popup shows. */
  previewUrlOf: (year: number) => string;
  /** Saved under this name plus the year. */
  fileNameOf: (year: number) => string;
}

/**
 * Pick one of the calendar years the donor gave in and get the donor's donations report: the eye
 * shows it in a popup inside the app, the arrow saves the PDF. Shared by the transactions board's
 * donor details and the management screen, which differ only in which report they ask for.
 *
 * The popup shows page images the server renders, not the PDF in an iframe: that leaned on the
 * browser's own PDF viewer, and a Chrome set to download PDFs instead - common on office machines -
 * showed a placeholder whose «פתיחה» did nothing.
 */
const DonorDonationsReportControl = ({ yearsUrl, reportUrlOf, previewUrlOf, fileNameOf }: DonorDonationsReportControlProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const { data: years, isLoading: areYearsLoading, isError: areYearsError } = useGetAuthApi<number[]>(yearsUrl);
  const [pickedYear, setPickedYear] = useState<number | null>(null);
  // The newest year with donations until the user picks another.
  const year = pickedYear ?? years?.[0] ?? null;
  const [isBusy, setIsBusy] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [previewPages, setPreviewPages] = useState<string[] | null>(null);

  const run = async (action: () => Promise<boolean>) => {
    if (isBusy) return;
    setIsBusy(true);
    setHasError(false);
    try {
      if (!(await action())) setHasError(true);
    } catch (error) {
      console.error('Failed to generate the donor donations report.', error);
      setHasError(true);
    } finally {
      setIsBusy(false);
    }
  };

  const token = currentUser?.token ?? '';

  if (areYearsLoading) {
    return <p className="body-sm-regular text-(--font-body-2)">{t('donorDonationsReport.loadingYears')}</p>;
  }

  if (areYearsError) {
    return (
      <p role="alert" className="body-sm-regular text-(--font-primary)">
        {t('donorDonationsReport.error')}
      </p>
    );
  }

  if (year == null) {
    return <p className="body-sm-regular text-(--font-body-2)">{t('donorDonationsReport.noDonations')}</p>;
  }

  const view = () =>
    run(async () => {
      const res = await apiClient.get<{ pages: string[] }>(previewUrlOf(year), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const pages = res.data?.pages ?? [];
      if (pages.length === 0) return false;
      setPreviewPages(pages);
      return true;
    });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 body-md-regular text-(--font-body-1)">
          {t('donorDonationsReport.year')}
          <select
            className="h-9 rounded-lg border border-(--border-main) bg-(--surface-base) px-2 body-md-regular text-(--font-heading)"
            value={year}
            onChange={(e) => setPickedYear(Number(e.target.value))}
            disabled={isBusy}
          >
            {years?.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <ActionIcon icon={eye} title={t('donorDonationsReport.view')} onClick={view} />
        <ActionIcon
          icon={download}
          title={t('donorDonationsReport.download')}
          onClick={() => run(() => downloadFileFromApi(reportUrlOf(year), `${fileNameOf(year)}.pdf`, token))}
        />
      </div>
      {isBusy && <p className="body-sm-regular text-(--font-body-2)">{t('donorDonationsReport.generating')}</p>}
      {hasError && (
        <p role="alert" className="body-sm-regular text-(--font-primary)">
          {t('donorDonationsReport.error')}
        </p>
      )}
      <CustomDialog
        open={previewPages != null}
        setOpen={(open) => {
          if (!open) setPreviewPages(null);
        }}
        hideActionButton
        // The dialog pads its body for short messages; a document wants the width.
        className="!min-w-0 w-[min(900px,94vw)] h-[90vh] !pb-4 [&>div]:!px-4 [&>div>div]:w-full"
      >
        {previewPages && (
          <div className="w-full h-[calc(90vh-80px)] overflow-y-auto bg-(--surface-action) p-3 flex flex-col items-center gap-3">
            {previewPages.map((page, i) => (
              <img
                key={i}
                src={page}
                alt={`${fileNameOf(year)} - ${i + 1}`}
                className="w-full max-w-[794px] shadow-md bg-white"
              />
            ))}
          </div>
        )}
      </CustomDialog>
    </div>
  );
};

export default DonorDonationsReportControl;
